import { useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";

import { useSetOrRemoveProviderApiKey } from "../../../queries";
import LoadingSpinner from "../../../components/LoadingSpinner";

import { useApiKeyStore } from "./store/api-key-store";

import { useAuth } from "@/context/auth-context";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const LoadingSkeleton = () => (
  <div className="max-w-[600px]">
    <div className="mt-1">
      <div className="flex w-full flex-col gap-1">
        <Skeleton className="h-5 w-full max-w-[100px]" />
        <Skeleton className="h-9 w-full max-w-[600px]" />
      </div>
      <div className="mt-2 flex w-full">
        <Skeleton className="h-10 w-full max-w-[200px]" />
      </div>
    </div>
  </div>
);

interface SimpleApiKeySectionProps {
  title: string;
  provider: string;
  placeholder: string;
  onAfterSaveSuccess?: () => void;
}

const SimpleApiKeySection = observer(
  ({ title, provider, placeholder, onAfterSaveSuccess }: SimpleApiKeySectionProps) => {
    const auth = useAuth();
    const userToken = auth?.userToken ?? "";
    const { workspaceId } = useParams();

    const store = useApiKeyStore();
    const apiKey = store.getApiKey(provider);
    const { isLoading } = store;
    const [inputApiKey, setInputApiKey] = useState("");

    const { mutateAsync, isPending } = useSetOrRemoveProviderApiKey(userToken);

    const suffixKey = `${provider}_suffix`;
    const suffix = store.getApiKeyConfig(suffixKey);

    const removeApiKeyMutation = async () => {
      await mutateAsync({
        api_key: null,
        provider,
        workspace_id: Number(workspaceId),
      }).then(response => {
        if (response.success) {
          store.setApiKey(provider, false);
          store.setApiKeyConfig(suffixKey, "");
          setInputApiKey("");
        }
      });
    };

    const saveAPIKey = async () => {
      if (!inputApiKey) {
        return;
      }
      await mutateAsync({
        api_key: inputApiKey,
        provider,
        workspace_id: Number(workspaceId),
      }).then(response => {
        if (response.success) {
          store.setApiKey(provider, true);
          store.setApiKeyConfig(suffixKey, inputApiKey.slice(-4));
          onAfterSaveSuccess?.();
        }
        setInputApiKey("");
      });
    };

    if (isLoading) {
      return <LoadingSkeleton />;
    }

    const savedDisplay = suffix ? `••••••••••••••••••••${suffix}` : "••••••••••••••••••••••••";

    return (
      <div className="mt-2 flex h-full flex-col">
        <div className="max-w-[600px]">
          <div className="mt-1">
            <Input
              label={title}
              autoComplete="off"
              type={apiKey ? "text" : "password"}
              name={`${provider}_api_key`}
              id={`${provider}_api_key`}
              placeholder={placeholder}
              value={apiKey ? savedDisplay : inputApiKey || ""}
              readOnly={!!apiKey}
              className={apiKey ? "font-mono" : undefined}
              onChange={e => setInputApiKey(e.target.value)}
            />

            <div className="justify- mt-2 flex w-full">
              {!apiKey ? (
                <Button onClick={saveAPIKey} disabled={!inputApiKey || isPending} variant="outline">
                  {isPending ? <LoadingSpinner size={5} /> : `Save ${title}`}
                </Button>
              ) : (
                <Button
                  variant="destructiveOutline"
                  onClick={removeApiKeyMutation}
                  disabled={isPending}
                >
                  Remove {title}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default SimpleApiKeySection;
